import React, {
  useState, useRef, useMemo, useEffect, useLayoutEffect, useCallback,
} from "react";

import { Input, Checkbox } from "antd";
import { debounce } from "lodash";
import { toast } from "react-toastify";

import AllProductList from "./AllProductList";
import { filterSpecManagerProductsBySearch } from "./Helper";
import ProductList from "./ProductList";
import ProductSuggestionList from "./ProductSuggestionList";

import { getAllProducts } from "../../../../../actions/specManager";

import searchIcon from "../../../../../assets/images/sample-submission/searchIcon.svg";

import "./UpdateProductList.css";

/**
 * Product list with search and select.
 * Mirrors UpdateTestList pattern - left: searchable suggestions, right: selected list.
 * Used in edit screen to list and select products.
 */
export default function UpdateProductList(props) {
  const {
    selectedProducts = [],
    onSelectedProductsChange,
    fields = [],
    linkReportJsonFields = [],
    layout = "side-by-side",
    searchValue,
    onSearchValueChange,
    onFooterStateChange,
    onRegisterAddProductsAction,
  } = props;

  const isStacked = layout === "stacked";

  const [search, setSearch] = useState(searchValue ?? "");
  const [searchTerm, setSearchTerm] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selected, setSelected] = useState(() => [...selectedProducts]);
  const [checkedProductIds, setCheckedProductIds] = useState(() => new Set());
  const [checkedAllProductIds, setCheckedAllProductIds] = useState(() => new Set());
  const [isLoading, setIsLoading] = useState(true);
  const productSuggestionsScrollbar = useRef();
  const onFooterStateChangeRef = useRef(onFooterStateChange);
  onFooterStateChangeRef.current = onFooterStateChange;

  const loading = isLoading;

  const selectedProductIdsSet = useMemo(
    () => new Set(selected.map((product) => product.product_id)),
    [selected],
  );

  /** Props-aligned set so footer / preview gate never lag behind parent when local `selected` state sync is one tick late. */
  const selectedIdsFromPropsSet = useMemo(
    () => new Set((selectedProducts ?? []).map((product) => product.product_id)),
    [selectedProducts],
  );

  const nonRemovableProductIds = useMemo(
    () => new Set(selected.filter((product) => product.test_submitted).map((product) => product.product_id)),
    [selected],
  );

  const filterProducts = useCallback(
    (products, term) => filterSpecManagerProductsBySearch(products, term, linkReportJsonFields),
    [linkReportJsonFields],
  );

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      setIsLoading(true);
      try {
        const result = await getAllProducts(controller.signal);
        if (cancelled) return;
        if (!result.success) {
          toast.error(result.message);
        }
        if (result.success && Array.isArray(result.products)) {
          setAllProducts(result.products);
        }
        if (productSuggestionsScrollbar.current?.scrollerElement) {
          productSuggestionsScrollbar.current.scrollerElement.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch (error) {
        if (cancelled) return;
        toast.error("Something went wrong.");
      }
      if (!cancelled) {
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    setFilteredProducts(filterProducts(allProducts, searchTerm));
  }, [allProducts, searchTerm, filterProducts]);

  useEffect(() => {
    setSelected([...selectedProducts]);
  }, [selectedProducts]);

  const filteredSelectedProducts = useMemo(
    () => (searchTerm && searchTerm.trim() ? filterProducts(selected, searchTerm) : selected),
    [searchTerm, selected, filterProducts],
  );

  // Checkbox state is for "selecting items" in the UI, not for removing from selectedProducts.
  const selectedIdsSet = useMemo(
    () => new Set(selected.map((product) => product.product_id)),
    [selected],
  );

  const removableSelectedIdsSet = useMemo(
    () => new Set(selected.filter((product) => !product.test_submitted).map((product) => product.product_id)),
    [selected],
  );

  useEffect(() => {
    setCheckedProductIds((prev) => {
      const next = new Set(prev);
      Array.from(next).forEach((id) => {
        if (!selectedIdsSet.has(id)) next.delete(id);
        if (!removableSelectedIdsSet.has(id)) next.delete(id);
      });
      return next;
    });
  }, [selectedIdsSet, removableSelectedIdsSet]);

  const removableFilteredSelectedProducts = filteredSelectedProducts.filter((product) => !product.test_submitted);

  const selectAllChecked = removableFilteredSelectedProducts.length > 0
    && removableFilteredSelectedProducts.every((product) => checkedProductIds.has(product.product_id));
  const selectAllIndeterminate = !selectAllChecked
    && removableFilteredSelectedProducts.some((product) => checkedProductIds.has(product.product_id));

  const handleSelectAllChange = (e) => {
    if (loading) return;
    const { checked } = e.target;
    setCheckedProductIds((prev) => {
      const next = new Set(prev);
      removableFilteredSelectedProducts.forEach((product) => {
        if (checked) next.add(product.product_id);
        else next.delete(product.product_id);
      });
      return next;
    });
  };

  /** Used when ProductList row checkboxes (and optional stacked Select All) are re-enabled; wire via ProductList props (see JSX comments). */
  // const handleToggleRowChecked = (product, nextChecked) => {
  //   if (loading) return;
  //   setCheckedProductIds((prev) => {
  //     const next = new Set(prev);
  //     if (nextChecked) next.add(product.product_id);
  //     else next.delete(product.product_id);
  //     return next;
  //   });
  // };

  const debouncedSearch = useMemo(() => debounce((term) => {
    setSearchTerm(term);
  }, 500), []);

  useEffect(() => {
    if (typeof searchValue !== "string") return;
    setSearch(searchValue);
    debouncedSearch(searchValue);
  }, [searchValue, debouncedSearch]);

  const handleChangeSearch = (e) => {
    const next = e.target.value;
    setSearch(next);
    debouncedSearch(next);
    if (onSearchValueChange) onSearchValueChange(next);
  };

  const handleHitEnter = (e) => {
    if (e.key === "Enter") {
      setSearchTerm(e.target.value);
    }
  };

  const handleAddProduct = (product) => {
    if (selectedProductIdsSet.has(product.product_id)) return;
    const next = [...selected, product];
    setSelected(next);
    if (onSelectedProductsChange) onSelectedProductsChange(next);
  };

  const handleRemoveProduct = (product) => {
    if (product.test_submitted) return;
    const next = selected.filter((prod) => prod.product_id !== product.product_id);
    setSelected(next);
    if (onSelectedProductsChange) onSelectedProductsChange(next);
  };

  const addCountFromAllList = useMemo(
    () => Array.from(checkedAllProductIds).filter((id) => !selectedIdsFromPropsSet.has(id)).length,
    [checkedAllProductIds, selectedIdsFromPropsSet],
  );

  const selectableFilteredProducts = useMemo(
    () => filteredProducts.filter((p) => !selectedIdsSet.has(p.product_id)),
    [filteredProducts, selectedIdsSet],
  );

  const selectAllAllChecked = selectableFilteredProducts.length > 0
    && selectableFilteredProducts.every((p) => checkedAllProductIds.has(p.product_id));
  const selectAllAllIndeterminate = !selectAllAllChecked
    && selectableFilteredProducts.some((p) => checkedAllProductIds.has(p.product_id));

  const handleSelectAllAllProductsChange = (e) => {
    if (loading) return;
    const { checked } = e.target;
    setCheckedAllProductIds((prev) => {
      const next = new Set(prev);
      selectableFilteredProducts.forEach((p) => {
        if (checked) next.add(p.product_id);
        else next.delete(p.product_id);
      });
      return next;
    });
  };

  useEffect(() => {
    setCheckedAllProductIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      selectedIdsSet.forEach((id) => {
        if (next.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [selectedIdsSet]);

  const handleToggleAllProductChecked = (product, nextChecked) => {
    if (loading) return;
    setCheckedAllProductIds((prev) => {
      const next = new Set(prev);
      if (nextChecked) next.add(product.product_id);
      else next.delete(product.product_id);
      return next;
    });
  };

  const handleAddCheckedToSelected = useCallback(() => {
    const toAdd = filteredProducts.filter(
      (p) => checkedAllProductIds.has(p.product_id) && !selectedIdsSet.has(p.product_id),
    );
    if (toAdd.length === 0) return;
    const next = [...selected];
    toAdd.forEach((p) => {
      if (!selectedIdsSet.has(p.product_id)) next.push(p);
    });
    setSelected(next);
    setCheckedAllProductIds(new Set());
    if (onSelectedProductsChange) onSelectedProductsChange(next);
  }, [
    filteredProducts,
    checkedAllProductIds,
    selected,
    selectedIdsSet,
    onSelectedProductsChange,
  ]);

  useEffect(() => {
    if (typeof onRegisterAddProductsAction === "function") {
      onRegisterAddProductsAction(handleAddCheckedToSelected);
    }
  }, [onRegisterAddProductsAction, handleAddCheckedToSelected]);

  useLayoutEffect(() => {
    const fn = onFooterStateChangeRef.current;
    if (typeof fn === "function") {
      fn({
        addCountFromAllList,
        selectedCount: selectedProducts?.length ?? 0,
        allProductsLoading: loading,
      });
    }
  }, [addCountFromAllList, selectedProducts, loading]);

  /** ?
   *? Clear all selected products option can be added if needed in the future,
   *? currently not added as it can cause accidental clearing of all selected products with one click,
   *? which can be frustrating if user has many products selected and forgets to save before clicking clear all.
   */

  // const handleClearAll = () => {
  //   setSelected([]);
  //   if (onSelectedProductsChange) onSelectedProductsChange([]);
  // };

  const renderSearchBox = () => (
    <div className="UpdateProductList__SearchBox">
      <img alt="search icon" src={searchIcon} />
      <Input
        placeholder="Search in Product Directory"
        onChange={handleChangeSearch}
        value={search}
        onKeyUp={handleHitEnter}
      />
    </div>
  );

  return (
    <div className={`UpdateProductList__Container ${isStacked ? "UpdateProductList__Container--stacked" : ""}`}>
      {isStacked ? (
        <>
          <div className="UpdateProductList__SelectedListContainer">
            <div className="UpdateProductList__SelectedListBox">
              <div className="UpdateProductList__SelectedListHeader">
                <h3 className="UpdateProductList__SelectedListTitle">
                  {`Selected Products (${selected.length})`}
                </h3>
                {/* <div className="UpdateProductList__SelectAllRow">
                  <Checkbox
                    checked={selectAllChecked}
                    indeterminate={selectAllIndeterminate}
                    onChange={handleSelectAllChange}
                    disabled={loading}
                  >
                    Select All
                  </Checkbox>
                </div> */}
              </div>
              <ProductList
                selectedProducts={filteredSelectedProducts}
                totalSelectedCount={selected.length}
                searchTerm={searchTerm}
                loading={loading}
                onRemoveProduct={handleRemoveProduct}
                fields={fields}
                linkReportJsonFields={linkReportJsonFields}
              />
            </div>
          </div>

          <div className="UpdateProductList__AllProductsContainer">
            <div className="UpdateProductList__AllProductsList">
              <div className="UpdateProductList__AllProductsListHeader">
                <h3 className="UpdateProductList__SectionTitle">All Products</h3>
                <div className="UpdateProductList__SelectAllRow">
                  <Checkbox
                    checked={selectAllAllChecked}
                    indeterminate={selectAllAllIndeterminate}
                    onChange={handleSelectAllAllProductsChange}
                    disabled={loading || selectableFilteredProducts.length === 0}
                  >
                    Select All
                  </Checkbox>
                </div>
              </div>
              <AllProductList
                products={filteredProducts}
                searchTerm={searchTerm}
                loading={loading}
                checkedProductIds={checkedAllProductIds}
                onToggleRowChecked={handleToggleAllProductChecked}
                onAddProduct={handleAddProduct}
                selectedProductIds={selectedProductIdsSet}
                fields={fields}
                linkReportJsonFields={linkReportJsonFields}
              />
            </div>
          </div>

        </>
      ) : (
        <>
          <div className="UpdateProductList__ProductListContainer">
            <div className="UpdateProductList__Header">
              <h3 className="UpdateProductList__SectionTitle">All Products</h3>
              {renderSearchBox()}
            </div>
            <ProductSuggestionList
              productSuggestionsScrollbar={productSuggestionsScrollbar}
              productSuggestions={filteredProducts}
              selectedProductIds={selectedProductIdsSet}
              nonRemovableProductIds={nonRemovableProductIds}
              searchTerm={searchTerm}
              isLoading={isLoading}
              loading={loading}
              onAddProduct={handleAddProduct}
              onRemoveProduct={handleRemoveProduct}
              fields={fields}
              linkReportJsonFields={linkReportJsonFields}
            />
          </div>

          <div className="UpdateProductList__SelectedListContainer">
            <div className="UpdateProductList__SelectedListBox">
              <div className="UpdateProductList__SelectedListHeader">
                <h3 className="UpdateProductList__SelectedListTitle">
                  {`Selected Products (${selected.length})`}
                </h3>
                <div className="UpdateProductList__SelectAllRow">
                  <Checkbox
                    checked={selectAllChecked}
                    indeterminate={selectAllIndeterminate}
                    onChange={handleSelectAllChange}
                    disabled={loading}
                  >
                    Select All
                  </Checkbox>
                </div>
              </div>
              {/*
                With ProductList row checkboxes uncommented, also pass:
                checkedProductIds={checkedProductIds}
                onToggleRowChecked={handleToggleRowChecked}
              */}
              <ProductList
                selectedProducts={filteredSelectedProducts}
                totalSelectedCount={selected.length}
                searchTerm={searchTerm}
                loading={loading}
                onRemoveProduct={handleRemoveProduct}
                fields={fields}
                linkReportJsonFields={linkReportJsonFields}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
